import { z } from "zod";
import { t } from "@smeetz/i18n";
import { email as emailPattern } from "@smeetz/services-validation";

export interface TeamMemberFormValues {
  roleType: number;
  email: string;
  socialSecurityNumber: string;
}

export interface TeamMemberSchemaOptions {
  /** Edit mode skips the email requirement (the email field is read-only). */
  isEdit: boolean;
  /** Lower-cased emails already in the team, for the create-only duplicate check. */
  existingEmails: string[];
}

/**
 * Zod schema for the team member create/edit form: role and email are
 * required (email only on create, where it is editable), the social security
 * number stays optional. Mirrors the back-office member form rules.
 *
 * @param {TeamMemberSchemaOptions} options - edit mode and duplicate-email data.
 * @returns {z.ZodTypeAny} schema validating the team member fields.
 */
export function teamMemberSchema(
  options: TeamMemberSchemaOptions
): z.ZodTypeAny {
  return z
    .object({
      roleType: z.number(),
      email: z.string().max(255, t("validations.length-max")),
      socialSecurityNumber: z.string().max(255, t("validations.length-max")),
    })
    .superRefine((values, ctx) => {
      if (!values.roleType) {
        ctx.addIssue({
          code: "custom",
          path: ["roleType"],
          message: t("validations.enter-value"),
        });
      }

      const email = values.email.trim();

      if (!options.isEdit && !email) {
        ctx.addIssue({
          code: "custom",
          path: ["email"],
          message: t("validations.enter-value"),
        });
      }
      if (email && !emailPattern.test(email)) {
        ctx.addIssue({
          code: "custom",
          path: ["email"],
          message: t("validations.enter-valid-value"),
        });
      }
      if (
        !options.isEdit &&
        email &&
        options.existingEmails.includes(email.toLowerCase())
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["email"],
          message: t("team.email-exists-in-team"),
        });
      }
    });
}
